/**
 * `/docs/quickstart` — extended tutorial: key → run → fetch → poll
 * → webhook. Each step gets a `CodeSnippet` with language tabs.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { useKeys } from '@/lib/queries';
import { Key } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/quickstart')({
  component: Quickstart,
});

function Quickstart(): ReactElement {
  const keys = useKeys();
  const prefix = keys.data?.[0]?.prefix;
  const vars = { API_KEY: prefix ?? '<YOUR_API_KEY>', URL: 'https://example.com' };

  return (
    <Island breadcrumb={[{ label: 'Docs', href: '/docs' }, { label: 'Quickstart' }]}>
      <div className="mx-auto max-w-3xl space-y-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Quickstart</h1>
          <p className="text-sm text-[var(--color-mute)]">
            From zero to a completed audit payload in five steps. Code is shown in cURL, Node,
            Python, and Go — pick your flavour.
          </p>
        </header>

        <Step
          number={1}
          title="Create an API key"
          body={
            <>
              <p className="text-sm text-[var(--color-mute)]">
                API keys carry a prefix (<span className="font-mono text-xs">sk_live_</span>) and
                are scoped per call. Mint yours in the dashboard — the secret is shown once.
              </p>
              <Button size="sm" variant="outline" render={<Link to="/settings/keys" />}>
                <Key aria-hidden="true" />
                Manage keys
              </Button>
            </>
          }
        />

        <Step
          number={2}
          title="POST an audit"
          body={
            <CodeSnippet
              variables={vars}
              languages={[
                {
                  id: 'curl',
                  label: 'cURL',
                  body:
                    'curl https://api.clickweave.co/v1/audits \\\n' +
                    "  -H 'Authorization: Bearer $API_KEY' \\\n" +
                    "  -H 'Content-Type: application/json' \\\n" +
                    '  -d \'{"url":"$URL"}\'',
                },
                {
                  id: 'node',
                  label: 'Node',
                  body:
                    "const res = await fetch('https://api.clickweave.co/v1/audits', {\n" +
                    "  method: 'POST',\n" +
                    '  headers: {\n' +
                    '    Authorization: `Bearer ${process.env.CLICKWEAVE_API_KEY}`,\n' +
                    "    'Content-Type': 'application/json',\n" +
                    '  },\n' +
                    "  body: JSON.stringify({ url: '$URL' }),\n" +
                    '});\n' +
                    'const { id } = await res.json();',
                },
                {
                  id: 'python',
                  label: 'Python',
                  body:
                    'import os, httpx\n\n' +
                    'res = httpx.post(\n' +
                    "    'https://api.clickweave.co/v1/audits',\n" +
                    "    headers={'Authorization': f\"Bearer {os.environ['CLICKWEAVE_API_KEY']}\"},\n" +
                    "    json={'url': '$URL'},\n" +
                    ')\n' +
                    "audit_id = res.json()['id']",
                },
                {
                  id: 'go',
                  label: 'Go',
                  body:
                    'body, _ := json.Marshal(map[string]string{"url": "$URL"})\n' +
                    'req, _ := http.NewRequest("POST", "https://api.clickweave.co/v1/audits", bytes.NewReader(body))\n' +
                    'req.Header.Set("Authorization", "Bearer "+os.Getenv("CLICKWEAVE_API_KEY"))\n' +
                    'req.Header.Set("Content-Type", "application/json")\n' +
                    'res, _ := http.DefaultClient.Do(req)',
                },
              ]}
            />
          }
        />

        <Step
          number={3}
          title="Poll until complete"
          body={
            <>
              <p className="text-sm text-[var(--color-mute)]">
                Audits run asynchronously — the POST returns immediately with
                <span className="font-mono text-xs"> status: pending</span>. Poll every few seconds
                until <span className="font-mono text-xs">status</span> is{' '}
                <span className="font-mono text-xs">success</span> or{' '}
                <span className="font-mono text-xs">error</span>.
              </p>
              <CodeSnippet
                variables={vars}
                code={
                  'curl https://api.clickweave.co/v1/audits/$AUDIT_ID \\\n' +
                  "  -H 'Authorization: Bearer $API_KEY'"
                }
              />
            </>
          }
        />

        <Step
          number={4}
          title="Fetch the report"
          body={
            <>
              <p className="text-sm text-[var(--color-mute)]">
                Full Lighthouse reports live on R2. Exchange the audit id for a presigned URL (300 s
                TTL) — cache and re-download as needed.
              </p>
              <CodeSnippet
                variables={vars}
                code={
                  'curl https://api.clickweave.co/v1/audits/$AUDIT_ID/report \\\n' +
                  "  -H 'Authorization: Bearer $API_KEY'"
                }
              />
            </>
          }
        />

        <Step
          number={5}
          title="Skip polling with a webhook"
          body={
            <>
              <p className="text-sm text-[var(--color-mute)]">
                Pass a <span className="font-mono text-xs">webhook_url</span> on the create call; we
                POST a signed payload the moment the audit resolves. See{' '}
                <Link to="/docs/webhooks" className="underline">
                  Webhooks
                </Link>{' '}
                for signature verification.
              </p>
              <CodeSnippet
                variables={vars}
                code={
                  'curl https://api.clickweave.co/v1/audits \\\n' +
                  "  -H 'Authorization: Bearer $API_KEY' \\\n" +
                  "  -H 'Content-Type: application/json' \\\n" +
                  '  -d \'{"url":"$URL","webhook_url":"https://your.app/webhooks/clickweave"}\''
                }
              />
            </>
          }
        />
      </div>
    </Island>
  );
}

function Step({
  number,
  title,
  body,
}: {
  number: number;
  title: string;
  body: ReactElement;
}): ReactElement {
  return (
    <section className="space-y-3">
      <header className="flex items-center gap-3">
        <span className="flex size-7 items-center justify-center rounded-full bg-[var(--color-accent-brand)] text-xs font-semibold text-white">
          {number}
        </span>
        <h2 className="text-lg font-semibold text-[var(--color-ink)]">{title}</h2>
      </header>
      <div className="space-y-3 pl-10">{body}</div>
    </section>
  );
}
